import React, { useState } from 'react';
import { Link, useLocation } from 'wouter';
import MentorshipAccessGate from '@/components/MentorshipAccessGate';
import { ArrowLeft, Info, MessageCircle, Calendar, Users, Search, MessageSquare, Lightbulb } from 'lucide-react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

// Simple KeyTakeaway component similar to what's used in peptide protocols
const KeyTakeaway = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="bg-blue-50 border-l-4 border-blue-500 rounded-r-md p-5 my-6">
      <div className="flex">
        <Lightbulb className="text-blue-500 h-6 w-6 mr-3 flex-shrink-0 mt-0.5" />
        <div className="text-blue-700 font-serif">
          {children}
        </div>
      </div>
    </div>
  );
};

interface QAItem {
  id: string;
  question: string;
  answer: React.ReactNode;
  tags: string[];
  date: string;
  category: string;
}

export default function QAPeptideProtocols() {
  const [_, navigate] = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  // Sample Q&A data
  const qaItems: QAItem[] = [
    {
      id: "q1",
      question: "What are the optimal dosing strategies for BPC-157 in inflammatory conditions?",
      answer: (
        <div className="space-y-4">
          <p>
            BPC-157 is typically administered at doses of 250-500mcg once or twice daily for inflammatory 
            conditions. The route of administration depends on the specific inflammatory condition being treated:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>Systemic inflammation:</strong> Subcutaneous injection is preferred, with doses of 
              250-500mcg once or twice daily.
            </li>
            <li>
              <strong>Localized inflammation (e.g., joints, tendons):</strong> Local injection near the 
              affected area can be more effective, typically 250mcg per site.
            </li>
            <li>
              <strong>GI tract inflammation:</strong> Oral administration using a stable form of BPC-157 
              (e.g., BPC-157 Arg) at doses of 500-1000mcg daily.
            </li>
          </ul>
          <p>
            Treatment duration typically ranges from 2-4 weeks for acute conditions, while chronic 
            inflammatory conditions may benefit from longer protocols with periodic breaks.
          </p>
          <KeyTakeaway>
            Monitoring inflammatory markers (CRP, ESR) and symptom journals can help optimize dosing 
            strategies for individual patients. Consider pulsed protocols (e.g., 3 weeks on, 1 week off) 
            for longer treatment courses to prevent receptor desensitization.
          </KeyTakeaway>
        </div>
      ),
      tags: ["BPC-157", "inflammation", "dosing"],
      date: "March 15, 2025",
      category: "Healing Peptides"
    },
    {
      id: "q2",
      question: "How should Thymosin Alpha-1 be integrated with other immunomodulatory peptides?",
      answer: (
        <div className="space-y-4">
          <p>
            Thymosin Alpha-1 (TA-1) can be effectively combined with other immunomodulatory peptides, 
            but timing and sequencing are important considerations:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>TA-1 with LL-37:</strong> These can be administered concurrently as they work through 
              different, complementary mechanisms. TA-1 primarily modulates T cell function while LL-37 has 
              broad antimicrobial and immunomodulatory actions.
            </li>
            <li>
              <strong>TA-1 with Thymosin Beta-4:</strong> These are often used together in immune 
              restoration protocols. Typical dosing is TA-1 at 1.5mg twice weekly and TB-4 at 750mcg 
              twice weekly.
            </li>
            <li>
              <strong>TA-1 with GHK-Cu:</strong> This combination provides immune balancing with 
              tissue regenerative effects. Administer on separate days for optimal effect.
            </li>
          </ul>
          <p>
            For autoimmune conditions, start with low doses of TA-1 (e.g., 0.5-1mg twice weekly) and 
            monitor for any disease flares before gradually increasing the dose.
          </p>
          <KeyTakeaway>
            When combining immunomodulatory peptides, create a structured protocol with clear 
            monitoring parameters. Consider baseline immune panels (complete blood count with 
            differential, T-cell subsets if available) before starting and at 1-month intervals 
            to assess response and guide adjustments.
          </KeyTakeaway>
        </div>
      ),
      tags: ["Thymosin Alpha-1", "immunomodulation", "combination therapy"],
      date: "February 28, 2025",
      category: "Immunomodulatory Peptides"
    },
    {
      id: "q3",
      question: "What is the evidence for using Epitalon in addressing telomere shortening?",
      answer: (
        <div className="space-y-4">
          <p>
            The evidence for Epitalon's effects on telomere length comes primarily from animal studies 
            and limited human research:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>Mechanism:</strong> Epitalon appears to activate telomerase, the enzyme 
              responsible for maintaining telomere length, by enhancing gene expression of 
              telomerase components.
            </li>
            <li>
              <strong>Animal studies:</strong> Research in rodents demonstrates increased telomerase 
              activity and telomere elongation with Epitalon administration, along with increased 
              lifespan (up to 33% in some studies).
            </li>
            <li>
              <strong>Human research:</strong> Limited studies from the Khavinson group suggest 
              telomere lengthening in human fibroblast cultures and some clinical benefits in 
              elderly subjects, including improved immune parameters and reduced mortality.
            </li>
          </ul>
          <p>
            Standard protocols typically involve 5-10mg daily for 10-20 days, repeated 1-2 times 
            annually. However, it's important to note that large-scale, independent clinical trials 
            are lacking.
          </p>
          <KeyTakeaway>
            While promising, the evidence for Epitalon's effects on human telomere length is not 
            yet robust enough for definitive clinical recommendations. Consider it an experimental 
            approach and monitor telomere length through specialized testing services if using 
            clinically. Combine with other well-established longevity interventions rather than 
            relying solely on Epitalon.
          </KeyTakeaway>
        </div>
      ),
      tags: ["Epitalon", "telomeres", "aging"],
      date: "January 10, 2025",
      category: "Longevity Peptides"
    }
  ];

  // Categories derived from QA items
  const categories = Array.from(new Set(qaItems.map(item => item.category)));

  // Filter QA items based on search query and category
  const filteredQAItems = qaItems.filter(item => {
    const matchesSearch = searchQuery === "" || 
      item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesCategory = activeCategory === null || item.category === activeCategory;
    
    return matchesSearch && matchesCategory;
  });

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    // Search is already handled via state
  }



  return (
    <MentorshipAccessGate>
      <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <div className="container mx-auto px-4 py-8 flex-grow max-w-5xl">
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/mentorship">Mentorship</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink href="/mentorship/qa-archives">Q&A Archives</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink>Peptide Protocol Q&A</BreadcrumbLink>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        
        <div className="flex justify-between items-center mb-6">
          <Button 
            variant="ghost" 
            className="flex items-center text-[#3498DB]"
            onClick={() => navigate("/mentorship")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Mentorship
          </Button>
          
          <div className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-medium">
            Q&A Archive
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="md:col-span-1">
            <div className="sticky top-20 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Search Questions</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSearch} className="space-y-4">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
                      <Input
                        type="text"
                        placeholder="Search questions..."
                        className="pl-9"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </div>
                    <Button type="submit" className="w-full">Search</Button>
                  </form>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>Categories</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <Button 
                      variant={activeCategory === null ? "default" : "outline"} 
                      className="w-full justify-start" 
                      onClick={() => setActiveCategory(null)}
                    >
                      All Categories
                    </Button>
                    {categories.map((category, index) => (
                      <Button 
                        key={index}
                        variant={activeCategory === category ? "default" : "outline"} 
                        className="w-full justify-start" 
                        onClick={() => setActiveCategory(category)}
                      >
                        {category}
                      </Button>
                    ))}
                  </div>
                </CardContent>
              </Card>
              
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-md">
                <div className="flex">
                  <Info className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-blue-800 mb-1">Submit a Question</h4>
                    <p className="text-sm text-blue-700">
                      Don't see your question? Submit it for our next Q&A session.
                    </p>
                    <button className="text-xs font-medium text-blue-800 mt-2 hover:underline">
                      Submit Question Form
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Main content */}
          <div className="md:col-span-3 space-y-6">
            <div>
              <h1 className="text-3xl md:text-4xl font-serif font-bold text-[#2C3E50] mb-4">
                Peptide Protocol Q&A Archive
              </h1>
              <p className="text-[#7F8C8D] mb-6">
                Browse expert answers to common questions about peptide therapy protocols,
                dosing strategies, and clinical applications.
              </p>
              <div className="flex items-center gap-3 mb-8">
                <div className="flex items-center text-sm text-[#7F8C8D]">
                  <MessageSquare className="h-4 w-4 mr-1" />
                  <span>{qaItems.length} Questions Answered</span>
                </div>
                <div className="w-1 h-1 rounded-full bg-gray-300"></div>
                <div className="flex items-center text-sm text-[#7F8C8D]">
                  <Calendar className="h-4 w-4 mr-1" />
                  <span>Updated Monthly</span>
                </div>
              </div>
            </div>
            
            <div className="bg-blue-50 p-4 rounded-lg mb-6">
              <p className="text-blue-700 text-center">
                Showing {filteredQAItems.length} of {qaItems.length} questions
                {activeCategory && <span> in <strong>{activeCategory}</strong></span>}
                {searchQuery && <span> matching "<strong>{searchQuery}</strong>"</span>}
              </p>
            </div>
            
            <Separator />
            
            {/* Q&A items */}
            <div className="space-y-6">
              {filteredQAItems.length > 0 ? (
                <Accordion type="single" collapsible className="w-full">
                  {filteredQAItems.map((item) => (
                    <AccordionItem key={item.id} value={item.id}>
                      <AccordionTrigger className="text-left font-serif font-medium text-lg text-[#2C3E50] hover:text-[#3498DB]">
                        {item.question}
                      </AccordionTrigger>
                      <AccordionContent className="prose prose-blue max-w-none">
                        <div className="flex flex-wrap gap-2 mb-4">
                          {item.tags.map((tag, index) => (
                            <span 
                              key={index}
                              className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs"
                              onClick={() => setSearchQuery(tag)}
                            >
                              {tag}
                            </span>
                          ))}
                          <span className="px-2 py-0.5 bg-gray-100 text-gray-800 rounded-full text-xs ml-auto">
                            {item.date}
                          </span>
                        </div>
                        {item.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              ) : (
                <div className="text-center py-12">
                  <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <Search className="h-8 w-8 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-medium text-gray-600 mb-2">No questions found</h3>
                  <p className="text-gray-500 mb-6">
                    Try adjusting your search or category filters
                  </p>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setSearchQuery("");
                      setActiveCategory(null);
                    }}
                  >
                    Reset Filters
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
    </MentorshipAccessGate>
  );
}